<template>
  <Modal title="" :max-width="860" :header-border="false" body-overflow="auto" :body-padding="false" @close="onClose">
    <template #header>
      <div class="cd__title-wrap">
        <span class="cd-source">
          <span class="cd-source__mark" style="background: #7c3aed; color: #fff;">
            <svg viewBox="0 0 16 16" fill="currentColor"><circle cx="8" cy="8" r="5.5"/></svg>
          </span>
          Gong
        </span>
        <div class="cd__name">
          {{ callTitle }}
          <a v-if="callUrl" :href="callUrl" target="_blank" rel="noopener" class="cd-arrow" title="Open in Gong"><AppIcon name="arrow_up_right" :size="14" /></a>
        </div>
        <div class="cd__sub">
          <template v-for="(seg, i) in subSegments" :key="i">
            <span v-if="i > 0" class="cd__dot" />
            <span>{{ seg }}</span>
          </template>
        </div>
      </div>
    </template>

    <div v-if="loading" class="gc-loading">Loading...</div>
    <div v-else-if="!callDetail" class="gc-loading">Gong call not found.</div>
    <div v-else class="gc-body">
      <!-- Participants -->
      <div v-if="participants.length" class="gc-section">
        <div class="cd-label">Participants</div>
        <div class="cd-contacts">
          <div v-for="p in participants" :key="p.id || p.email" class="cd-contact">
            <span class="cd-avatar">{{ initials(p) }}</span>
            <div class="cd-contact__main">
              <div class="cd-contact__name">{{ p.name || p.email || 'Unknown' }}</div>
              <div v-if="p.title || p.email" class="cd-contact__title">{{ p.title || p.email }}</div>
            </div>
            <span v-if="p.is_internal === true" class="gc-badge gc-badge--internal">Internal</span>
            <span v-else-if="p.is_internal === false" class="gc-badge gc-badge--external">External</span>
          </div>
        </div>
      </div>

      <!-- AI Summary -->
      <div class="gc-section">
        <div class="cd-label">AI Summary</div>
        <div v-if="summaryLoading" class="gc-muted">Loading summary...</div>
        <div v-else-if="summaryData?.brief" class="gc-summary">
          <div class="gc-summary__brief">{{ summaryData.brief }}</div>
          <div v-if="keyPoints.length" class="gc-summary__group">
            <div class="cd-note__k">Key Points</div>
            <ul class="gc-list">
              <li v-for="(point, idx) in keyPoints" :key="idx">{{ point }}</li>
            </ul>
          </div>
        </div>
        <div v-else class="gc-muted">No summary available.</div>
      </div>

      <!-- Transcript -->
      <div class="gc-section">
        <div class="cd-label">Transcript</div>
        <div v-if="transcriptLoading" class="gc-muted">Loading transcript...</div>
        <pre v-else-if="formattedTranscript" class="gc-transcript">{{ formattedTranscript }}</pre>
        <div v-else class="gc-muted">No transcript available.</div>
      </div>
    </div>

    <template #footer>
      <button class="cd-btn cd-btn--ghost" @click="onClose">Close</button>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { computed, ref, watch } from 'vue'
import Modal from '../../next/components/Modal.vue'
import AppIcon from '../../components/AppIcon.vue'
import { apiFetch } from '../../services/api'
import { useContextDrawer } from '../useContextDrawer'

const { contextGeneration } = useContextDrawer()

const props = defineProps<{
  callId: string
  onClose: () => void
}>()

const loading = ref(false)
const callDetail = ref<any>(null)
const summaryLoading = ref(false)
const summaryData = ref<any>(null)
const transcriptLoading = ref(false)
const formattedTranscript = ref('')

const callTitle = computed(() => String(callDetail.value?.call?.title || 'Gong Call'))
const callUrl = computed(() => String(callDetail.value?.call?.url || ''))
const participants = computed(() => Array.isArray(callDetail.value?.participants) ? callDetail.value.participants : [])

const keyPoints = computed(() => {
  const pts = summaryData.value?.key_points
  return Array.isArray(pts) ? pts : []
})

const subSegments = computed(() => {
  const parts: string[] = []
  const startedAt = callDetail.value?.call?.started_at
  if (startedAt) {
    try {
      const dt = new Date(startedAt)
      parts.push(dt.toLocaleDateString([], { month: 'short', day: 'numeric', year: 'numeric' }))
      parts.push(dt.toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' }))
    } catch { /* skip */ }
  }
  const direction = String(callDetail.value?.call?.direction || '').trim()
  if (direction) parts.push(direction)
  const dur = formatDuration(callDetail.value?.call?.duration_seconds)
  if (dur) parts.push(dur)
  return parts
})

function initials(p: any): string {
  const name = String(p.name || p.email || '').trim()
  return name.split(' ').map((w: string) => w[0]).join('').slice(0, 2).toUpperCase() || '?'
}

function formatDuration(value: any): string {
  const n = Number(value || 0)
  if (!Number.isFinite(n) || n <= 0) return ''
  const minutes = Math.floor(n / 60)
  const seconds = n % 60
  if (minutes <= 0) return `${seconds}s`
  if (seconds <= 0) return `${minutes}m`
  return `${minutes}m ${seconds}s`
}

async function loadCall(id: string) {
  const callId = String(id || '').trim()
  if (!callId) return
  const gen = contextGeneration.value
  loading.value = true
  try {
    const result = await apiFetch(`/gong/calls/${encodeURIComponent(callId)}`)
    if (gen !== contextGeneration.value) return
    callDetail.value = result
  } catch {
    if (gen !== contextGeneration.value) return
    callDetail.value = null
  } finally {
    if (gen === contextGeneration.value) {
      loading.value = false
    }
  }
}

async function loadSummary() {
  const callId = String(props.callId || callDetail.value?.call?.id || '').trim()
  if (!callId) return
  const gen = contextGeneration.value
  summaryLoading.value = true
  try {
    const resp = await apiFetch(`/gong/calls/${encodeURIComponent(callId)}/summary`)
    if (gen !== contextGeneration.value) return
    summaryData.value = resp?.summary || null
  } catch {
    if (gen !== contextGeneration.value) return
    summaryData.value = null
  } finally {
    if (gen === contextGeneration.value) {
      summaryLoading.value = false
    }
  }
}

async function loadTranscript() {
  const callId = String(props.callId || callDetail.value?.call?.id || '').trim()
  if (!callId) return
  const gen = contextGeneration.value
  transcriptLoading.value = true
  try {
    const resp = await apiFetch(`/gong/calls/${encodeURIComponent(callId)}/transcript`)
    if (gen !== contextGeneration.value) return
    formattedTranscript.value = String(resp?.formatted_transcript || '')
  } catch {
    if (gen !== contextGeneration.value) return
    formattedTranscript.value = ''
  } finally {
    if (gen === contextGeneration.value) {
      transcriptLoading.value = false
    }
  }
}

watch(() => props.callId, async (nextId) => {
  await loadCall(String(nextId || ''))
}, { immediate: true })

watch(() => callDetail.value?.call?.id, async (nextId) => {
  if (!nextId) return
  await Promise.all([loadSummary(), loadTranscript()])
})
</script>

<style scoped>
.gc-loading {
  padding: var(--space-5);
  color: var(--color-text-tertiary);
  font-size: var(--fs-sm);
}

.gc-body {
  display: flex;
  flex-direction: column;
  gap: var(--space-4);
  padding: var(--space-5);
}

.gc-section {
  display: flex;
  flex-direction: column;
}

.gc-section :deep(.cd-label) {
  margin-bottom: var(--space-2);
}

.gc-muted {
  font-size: var(--fs-sm);
  color: var(--color-text-tertiary);
  font-style: italic;
}

.gc-badge {
  font-size: 11px;
  font-weight: var(--fw-semibold);
  text-transform: uppercase;
  border-radius: var(--radius-full);
  padding: 3px 9px;
  white-space: nowrap;
}

.gc-badge--internal {
  background: rgba(59, 130, 246, 0.12);
  color: #2563eb;
}

.gc-badge--external {
  background: rgba(16, 185, 129, 0.12);
  color: #059669;
}

.gc-summary {
  display: flex;
  flex-direction: column;
  gap: var(--space-3);
}

.gc-summary__brief {
  font-size: var(--fs-sm);
  font-weight: var(--fw-medium);
  line-height: var(--lh-normal);
  color: var(--color-text-primary);
}

.gc-summary__group {
  display: flex;
  flex-direction: column;
  gap: var(--space-2);
}

.gc-list {
  margin: 0;
  padding-left: 18px;
  font-size: var(--fs-sm);
  line-height: var(--lh-loose);
  color: var(--color-text-primary);
}

.gc-transcript {
  margin: 0;
  padding: var(--space-3);
  border: 1px solid var(--color-border-subtle);
  border-radius: var(--radius-md);
  background: var(--color-bg-subtle);
  font-size: var(--fs-xs);
  line-height: var(--lh-normal);
  color: var(--color-text-primary);
  white-space: pre-wrap;
  word-break: break-word;
}
</style>
